import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Compartment, EditorState } from '@codemirror/state';
import { EditorView, keymap, lineNumbers } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { yaml } from '@codemirror/lang-yaml';
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { Layout } from '../components/Layout';
import { useApiFetch } from '../hooks/useApiFetch';
import { useIsDarkTheme } from '../hooks/useIsDarkTheme';
import { useEvalSetConfig } from '../hooks/useEvalSetConfig';
import { parseYaml, dumpYaml } from '../utils/yaml';

const DEFAULT_YAML = `tasks:
  - package: ""
    name: ""
    items:
      - name: ""
models:
  - package: ""
    name: ""
    items:
      - name: ""
limit: 1
`;

interface FormFields {
  eval_set_id: string;
  name: string;
  limit: string;
  epochs: string;
}

interface SecretDeclaration {
  name: string;
  description?: string;
}

function extractSecrets(config: Record<string, unknown>): SecretDeclaration[] {
  const secretsMap = new Map<string, SecretDeclaration>();

  const addSecrets = (arr: unknown) => {
    if (!Array.isArray(arr)) return;
    for (const s of arr) {
      if (s && typeof s === 'object' && 'name' in s) {
        secretsMap.set(s.name as string, s as SecretDeclaration);
      }
    }
  };

  const runner = config.runner as Record<string, unknown> | undefined;
  addSecrets(runner?.secrets);

  const tasks = config.tasks as Record<string, unknown>[] | undefined;
  if (Array.isArray(tasks)) {
    for (const task of tasks) {
      const items = task.items as Record<string, unknown>[] | undefined;
      if (Array.isArray(items)) {
        for (const item of items) {
          addSecrets(item.secrets);
        }
      }
    }
  }

  return Array.from(secretsMap.values());
}

interface CreateEvalSetResponse {
  eval_set_id?: string;
  id?: string;
}

/** Recursively strip null values and empty containers from a config object. */
function stripDefaults(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    const filtered = obj.map(stripDefaults).filter(v => v != null);
    return filtered.length > 0 ? filtered : undefined;
  }
  if (obj && typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const stripped = stripDefaults(value);
      if (stripped != null) {
        result[key] = stripped;
      }
    }
    return Object.keys(result).length > 0 ? result : undefined;
  }
  return obj;
}

function extractFormFields(obj: Record<string, unknown>): FormFields {
  return {
    eval_set_id: String(obj.eval_set_id ?? ''),
    name: String(obj.name ?? ''),
    limit: obj.limit != null ? String(obj.limit) : '',
    epochs: obj.epochs != null ? String(obj.epochs) : '',
  };
}

function applyFormFieldToConfig(
  config: Record<string, unknown>,
  field: keyof FormFields,
  value: string
): Record<string, unknown> {
  const updated = { ...config };

  switch (field) {
    case 'eval_set_id':
      if (value) {
        updated.eval_set_id = value;
      } else {
        delete updated.eval_set_id;
      }
      break;

    case 'name':
      if (value) {
        updated.name = value;
      } else {
        delete updated.name;
      }
      break;

    case 'limit': {
      const n = Number(value);
      if (value && !Number.isNaN(n)) {
        updated.limit = n;
      } else {
        delete updated.limit;
      }
      break;
    }

    case 'epochs': {
      const n = Number(value);
      if (value && !Number.isNaN(n)) {
        updated.epochs = n;
      } else {
        delete updated.epochs;
      }
      break;
    }
  }

  return updated;
}

/** Read the current YAML text from the CodeMirror editor, or fall back to state. */
function getEditorText(viewRef: React.RefObject<EditorView | null>): string {
  return viewRef.current?.state.doc.toString() ?? '';
}

/**
 * CodeMirror theme for the YAML editor. CodeMirror styles its own surface
 * (it isn't reachable by Tailwind's `dark:`), so we feed it explicit colors
 * and the `dark` flag. Swapped at runtime via a Compartment on theme change.
 */
const editorTheme = (dark: boolean) => {
  const styles: Record<string, Record<string, string>> = {
    '&': { height: '100%' },
    '.cm-scroller': { overflow: 'auto' },
    '.cm-content': { fontFamily: 'monospace', fontSize: '13px' },
    '.cm-gutters': dark
      ? {
          backgroundColor: '#0b1220',
          color: '#6b7280',
          borderRight: '1px solid #1f2937',
        }
      : { backgroundColor: '#f9fafb', borderRight: '1px solid #e5e7eb' },
  };
  if (dark) {
    styles['&'].backgroundColor = '#0b1220';
    styles['&'].color = '#e5e7eb';
  }
  return EditorView.theme(styles, { dark });
};

export default function LaunchPage() {
  const [searchParams] = useSearchParams();
  const cloneId = searchParams.get('clone');
  const {
    config: clonedConfig,
    isLoading: cloneLoading,
    error: cloneError,
  } = useEvalSetConfig(cloneId);

  const [fields, setFields] = useState<FormFields>({
    eval_set_id: '',
    name: '',
    limit: '1',
    epochs: '',
  });
  const [yamlText, setYamlText] = useState(DEFAULT_YAML);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [detectedSecrets, setDetectedSecrets] = useState<SecretDeclaration[]>(
    []
  );
  const [secretValues, setSecretValues] = useState<Record<string, string>>({});
  const [yamlParseError, setYamlParseError] = useState<string | null>(null);

  const editorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const themeCompartment = useRef(new Compartment());
  // Latest resolved dark/light, read when (re)creating the editor without
  // making the editor's init effect depend on the theme. Seeded from the live
  // attribute so the first editor mount isn't a frame behind the theme.
  const isDarkRef = useRef(
    document.documentElement.getAttribute('data-bs-theme') === 'dark'
  );
  const syncSourceRef = useRef<'form' | 'editor' | null>(null);
  const submittingRef = useRef(false);
  const yamlTextRef = useRef(yamlText);

  const { apiFetch } = useApiFetch();
  const isDark = useIsDarkTheme();

  // Keep yamlTextRef in sync so the editor init always reads the latest value
  useEffect(() => {
    yamlTextRef.current = yamlText;
  }, [yamlText]);

  // Initialize with cloned config — retries if editor not yet mounted
  const [prevCloneId, setPrevCloneId] = useState<string | null>(null);
  if (cloneId && clonedConfig && prevCloneId !== cloneId) {
    setPrevCloneId(cloneId);

    const configCopy = (stripDefaults({
      ...clonedConfig,
      eval_set_id: undefined,
    }) ?? {}) as Record<string, unknown>;

    const newYaml = dumpYaml(configCopy);
    setYamlText(newYaml);
    setFields(extractFormFields(configCopy));
    setDetectedSecrets(extractSecrets(configCopy));
  }

  // Sync editor content when clone initializes
  useEffect(() => {
    if (!cloneId || !clonedConfig || !viewRef.current) return;

    const configCopy = (stripDefaults({
      ...clonedConfig,
      eval_set_id: undefined,
    }) ?? {}) as Record<string, unknown>;

    const newYaml = dumpYaml(configCopy);
    const view = viewRef.current;
    syncSourceRef.current = 'form';
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: newYaml },
    });
    syncSourceRef.current = null;
  }, [cloneId, clonedConfig]);

  // Initialize CodeMirror
  useEffect(() => {
    if (!editorRef.current || viewRef.current) return;

    const state = EditorState.create({
      doc: yamlTextRef.current,
      extensions: [
        lineNumbers(),
        history(),
        yaml(),
        highlightSelectionMatches(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        keymap.of([...defaultKeymap, ...historyKeymap, ...searchKeymap] as any),
        EditorView.updateListener.of(update => {
          if (update.docChanged) {
            const newText = update.state.doc.toString();
            if (syncSourceRef.current === 'form') return;
            syncSourceRef.current = 'editor';
            setYamlText(newText);
            try {
              const parsed = parseYaml(newText);
              setYamlParseError(null);
              if (parsed && typeof parsed === 'object') {
                const obj = parsed as Record<string, unknown>;
                setFields(extractFormFields(obj));
                setDetectedSecrets(extractSecrets(obj));
              }
            } catch (err) {
              setYamlParseError(
                err instanceof Error ? err.message : 'Invalid YAML'
              );
            }
            syncSourceRef.current = null;
          }
        }),
        // Seed with the current theme; the effect below keeps it in sync.
        // `isDarkRef` (not `isDark`) so a theme flip doesn't recreate the
        // editor and discard undo history / cursor.
        themeCompartment.current.of(editorTheme(isDarkRef.current)),
      ],
    });

    viewRef.current = new EditorView({ state, parent: editorRef.current });

    return () => {
      viewRef.current?.destroy();
      viewRef.current = null;
    };
    // Re-run when cloneLoading changes (editor div isn't in DOM during loading state)
  }, [cloneLoading]);

  // Follow the global theme (useIsDarkTheme observes [data-bs-theme]): swap the
  // editor's theme compartment whenever the resolved dark/light flips, and keep
  // isDarkRef current so a later editor re-create seeds the right theme.
  useEffect(() => {
    isDarkRef.current = isDark;
    viewRef.current?.dispatch({
      effects: themeCompartment.current.reconfigure(editorTheme(isDark)),
    });
  }, [isDark]);

  const handleFieldChange = useCallback(
    (field: keyof FormFields, value: string) => {
      setFields(prev => ({ ...prev, [field]: value }));

      try {
        const currentText = getEditorText(viewRef) || yamlText;
        const currentConfig =
          (parseYaml(currentText) as Record<string, unknown>) ?? {};
        const updatedConfig = applyFormFieldToConfig(
          currentConfig,
          field,
          value
        );
        const newYaml = dumpYaml(updatedConfig);
        setYamlText(newYaml);

        if (viewRef.current) {
          syncSourceRef.current = 'form';
          const view = viewRef.current;
          view.dispatch({
            changes: {
              from: 0,
              to: view.state.doc.length,
              insert: newYaml,
            },
          });
          syncSourceRef.current = null;
        }
      } catch {
        // If current YAML is unparseable, just update the field
      }
    },
    [yamlText]
  );

  const handleSubmit = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      let parsedConfig: Record<string, unknown>;
      try {
        const currentText = getEditorText(viewRef) || yamlText;
        const parsed = parseYaml(currentText);
        if (!parsed || typeof parsed !== 'object') {
          setSubmitError('Invalid YAML configuration');
          return;
        }
        parsedConfig = parsed as Record<string, unknown>;
      } catch (err) {
        setSubmitError(
          `YAML parse error: ${err instanceof Error ? err.message : String(err)}`
        );
        return;
      }

      const secretsPayload = Object.fromEntries(
        Object.entries(secretValues).filter(([, v]) => v.length > 0)
      );

      const body: Record<string, unknown> = {
        eval_set_config: parsedConfig,
        secrets:
          Object.keys(secretsPayload).length > 0 ? secretsPayload : undefined,
      };

      const response = await apiFetch('/eval_sets/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response) {
        try {
          const data: CreateEvalSetResponse = await response.json();
          const evalSetId = data.eval_set_id ?? data.id;
          if (evalSetId) {
            window.location.href = `/eval-set/${evalSetId}`;
            return;
          }
          setSubmitError('Launch succeeded but no eval set ID returned');
        } catch {
          setSubmitError('Unexpected response from server');
        }
      } else {
        setSubmitError('Failed to launch eval set. Check your configuration.');
      }
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [yamlText, apiFetch, secretValues]);

  if (cloneId && cloneLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500 dark:text-gray-400">
            Loading configuration...
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="shrink-0 px-6 py-4 border-b border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-950">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Launch Eval Set
          </h1>
          {cloneId && cloneError && (
            <p className="text-sm text-red-600 dark:text-red-400 mt-1">
              Failed to load config for {cloneId}
            </p>
          )}
          {cloneId && !cloneError && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Cloning from {cloneId}
            </p>
          )}
        </div>

        {/* Two-column layout */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
          {/* Left column — Form fields */}
          <div className="w-full md:w-2/5 border-r border-gray-200 dark:border-gray-800 overflow-y-auto p-6">
            <div className="space-y-4 max-w-md">
              <FormInput
                label="Eval Set ID"
                value={fields.eval_set_id}
                onChange={v => handleFieldChange('eval_set_id', v)}
                placeholder="my-eval-set-abc123"
              />
              <FormInput
                label="Name"
                value={fields.name}
                onChange={v => handleFieldChange('name', v)}
                placeholder="my-eval-set"
              />
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Limit"
                  value={fields.limit}
                  onChange={v => handleFieldChange('limit', v)}
                  type="number"
                  placeholder="1"
                />
                <FormInput
                  label="Epochs"
                  value={fields.epochs}
                  onChange={v => handleFieldChange('epochs', v)}
                  type="number"
                  placeholder="1"
                />
              </div>

              {detectedSecrets.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-800 pt-4">
                    Secrets
                  </h3>
                  {detectedSecrets.map(secret => (
                    <div key={secret.name}>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        {secret.name}
                        {secret.description && (
                          <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal">
                            — {secret.description}
                          </span>
                        )}
                      </label>
                      <input
                        type="password"
                        value={secretValues[secret.name] || ''}
                        onChange={e =>
                          setSecretValues(prev => ({
                            ...prev,
                            [secret.name]: e.target.value,
                          }))
                        }
                        placeholder={`Enter ${secret.name}`}
                        className="w-full h-9 px-3 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-600 focus:border-brand-600 bg-white font-mono dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                      />
                    </div>
                  ))}
                </div>
              )}

              {yamlParseError && (
                <div className="text-sm text-red-600 dark:text-red-400">
                  <span className="font-medium">YAML error:</span>{' '}
                  {yamlParseError}
                </div>
              )}
            </div>
          </div>

          {/* Right column — YAML editor */}
          <div className="w-full md:w-3/5 flex flex-col overflow-hidden">
            <div className="shrink-0 px-4 py-2 bg-gray-50 border-b border-gray-200 dark:bg-gray-950 dark:border-gray-800">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                YAML Configuration
              </span>
            </div>
            <div ref={editorRef} className="flex-1 overflow-hidden" />
          </div>
        </div>

        {/* Bottom action bar */}
        <div className="shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-950 flex items-center gap-4">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !!yamlParseError}
            className="px-5 py-2 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-brand-600 hover:bg-brand-700 disabled:bg-gray-500"
          >
            {isSubmitting ? 'Launching...' : 'Launch Eval Set'}
          </button>
          {submitError && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {submitError}
            </p>
          )}
        </div>
      </div>
    </Layout>
  );
}

function FormInput({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-9 px-3 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
      />
    </div>
  );
}
